import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { TabBar } from '@/components/admin/sessions/sessions-tabs';
import { SessionToc, TOC_RESERVE } from '@/components/admin/sessions/walk/session-toc';
import { SessionVariables } from '@/components/admin/sessions/walk/session-variables';
import { SessionWalk } from '@/components/admin/sessions/walk/session-walk';
import type { BlockTrialSlot } from '@/components/admin/sessions/walk/walk-block-parts';
import type { HeadNoyauTarget } from '@/lib/session-head';
import type { SessionTocTrials } from '@/lib/session-toc';
import { cn } from '@/lib/utils';
import type { SessionDetail } from '@matter_extractor/shared';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

/**
 * The two tabs under the eight figures of a session (§12.2, issue #172):
 * **Parcours**, open by default - the fil of the pages, then the dry trials
 * (#220) - and **Variables**, the inventory.
 *
 * ## The fil comes first, the trials after it (#220)
 *
 * The fil says what the session DID, and it is what somebody opening a parcours
 * reads: it stands right under the figures. What the operator DOES to the session
 * - the dry reading of the analyst, the dry rangement, the group the route hands
 * over as `trial` - follows it. The pills of the head still lead to their block
 * and unfold it (#219): the scroll below runs once the block is drawn open.
 *
 * ## The tab is the route's, the ring is this component's
 *
 * The tab travels in the URL (`?tab=variables`, Parcours writing none), so it
 * comes in as a prop and leaves by `onSelectTab`: a pasted link opens the right
 * tab, and this component never learns the route. What it holds itself is the
 * one thing no URL should carry - which card to ring on the way back from a
 * line of the inventory. The ring lasts 1.8 s, the maquette's, and is then
 * dropped, so a card is never left ringed by a reload of the query.
 *
 * ## One tab mounted at a time
 *
 * The maquette hides the other panel; this unmounts it. The walk is drawn from
 * the detail already in the cache and the trial reads under its own keys, so
 * nothing is fetched twice - and a card that MOUNTS with its ring plays the
 * animation without any class juggling to restart it.
 */

export const SESSION_READING_TABS = [
  { id: 'parcours', label: 'Parcours' },
  { id: 'variables', label: 'Variables' },
] as const;

export type SessionReadingTab = (typeof SESSION_READING_TABS)[number]['id'];

/** How long a card stays ringed: the maquette's animation. */
const FLASH_MS = 1800;

export interface SessionReadingProps {
  readonly detail: SessionDetail;
  readonly tab: SessionReadingTab;
  readonly onSelectTab: (tab: SessionReadingTab) => void;
  /**
   * The group « Essais à blanc sur cette session » (noyau.md §9.4, #220) - the sections
   * « Lecture à blanc » and « Rangement à blanc », or the note of the vitrine - mounted by the
   * route and drawn AFTER the fil: it reads and writes trials through the query client, which the
   * walk alone does not need (`sessions-parcours.test.tsx` mounts it without one). Since #224 it
   * holds only the trials whose block the session does not have: the others stand in the foot of
   * their block (`noyauTrial`, `rangementTrial`).
   */
  readonly trial?: ReactNode;
  /** The dry trial of the analyst, drawn in the foot of the block « Détermination du noyau » (#224). */
  readonly noyauTrial?: BlockTrialSlot | undefined;
  /** The dry trial of the ranger, drawn in the foot of the block « Rangement de la matière » (#224). */
  readonly rangementTrial?: BlockTrialSlot | undefined;
  /**
   * The block the pill of the head led to (#218), null at rest: the tab is the route's, so the
   * route asks for Parcours, and once it is drawn this scrolls to the block. The route times
   * the ring and hands it to the section of the trial itself (`trial`); the block of the
   * determination gets it from here.
   */
  readonly focusNoyau?: HeadNoyauTarget | null;
  /**
   * Which of the sections « Lecture à blanc » and « Rangement à blanc » are mounted in `trial`
   * (#221, #224): the sommaire lists them only then. The route knows - a protocol was played, the
   * session has no block for that trial, and the vitrine has a note in their place - where
   * `trial` alone cannot say.
   */
  readonly tocTrials?: SessionTocTrials;
}

/** The mark of each block the pill of the head leads to. */
const NOYAU_TARGET_TEST_ID: Readonly<Record<HeadNoyauTarget, string>> = {
  determination: SESSIONS_TEST_IDS.walkNoyau,
  trial: SESSIONS_TEST_IDS.noyauTrial,
};

/** No section of the trials in the sommaire: the default of a reading mounted without the route's group. */
const NO_TOC_TRIALS: SessionTocTrials = { noyau: false, rangement: false };

export function SessionReading({
  detail,
  tab,
  onSelectTab,
  trial = null,
  noyauTrial,
  rangementTrial,
  focusNoyau = null,
  tocTrials = NO_TOC_TRIALS,
}: SessionReadingProps) {
  const [flash, setFlash] = useState<number | null>(null);
  // « ⓘ Comment le lire » (#224): the legend of the hour of arrival, closed until asked for.
  const [legend, setLegend] = useState(false);
  const legendId = useId();
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // The switch lands through the router, so the tab may still be Variables
    // on the render that set the ring: wait for the walk to be mounted.
    if (tab !== 'parcours' || flash === null) return;
    const card = root.current?.querySelector(`[data-visit="${flash}"]`);
    // Optional call: jsdom has no `scrollIntoView`.
    card?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(() => setFlash(null), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [tab, flash]);

  useEffect(() => {
    // The ring of a block of Parcours: wait for the walk to be mounted, as above.
    if (tab !== 'parcours' || focusNoyau === null) return;
    const block = root.current?.querySelector(
      `[data-testid="${NOYAU_TARGET_TEST_ID[focusNoyau]}"]`,
    );
    block?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [tab, focusNoyau]);

  const select = (next: SessionReadingTab): void => {
    setFlash(null);
    onSelectTab(next);
  };

  return (
    // `relative` and the reserve on the right are the sommaire's (#221): Parcours only.
    <div
      ref={root}
      className={cn('relative flex flex-col gap-5', tab === 'parcours' ? TOC_RESERVE : null)}
    >
      <TabBar
        tabs={SESSION_READING_TABS}
        active={tab}
        onSelect={select}
        testId={SESSIONS_TEST_IDS.walkTabs}
        tabTestId={SESSIONS_TEST_IDS.walkTab}
      />

      {tab === 'variables' ? (
        <SessionVariables
          entries={detail.variables}
          retained={detail.noyau?.retained ?? null}
          onOpenPage={(visit) => {
            setFlash(visit);
            onSelectTab('parcours');
          }}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h2 className="font-semibold text-gray-600 text-micro uppercase tracking-wide">
              Le parcours
            </h2>
            <button
              type="button"
              data-testid={SESSIONS_TEST_IDS.walkLegendToggle}
              aria-expanded={legend}
              aria-controls={legend ? legendId : undefined}
              onClick={() => setLegend((now) => !now)}
              className="inline-flex h-9 cursor-pointer items-center rounded-full border border-gray-200 bg-white px-3 text-caption text-gray-700 hover:border-bleu-nuit hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
            >
              <span aria-hidden="true">ⓘ</span>&nbsp;Comment le lire
            </button>
          </div>
          {legend ? (
            <p
              id={legendId}
              role="note"
              data-testid={SESSIONS_TEST_IDS.walkLegend}
              className="max-w-[46em] rounded-md border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-caption text-gray-700 leading-relaxed"
            >
              L’heure d’arrivée sur une page est celle de l’avancée qui l’a servie : la session
              horodate chaque avancée, une ligne par page traversée. Une page entrée deux fois - une
              reprise - apparaît deux fois.
              {detail.noyau === null
                ? null
                : ' Sur une carte de noyau, seul l’opérateur voit le nom, le triplet et la couleur : la personne n’a jamais vu que le texte. Le second choix est enregistré pour la mesure ; il ne change jamais le noyau retenu.'}
            </p>
          ) : null}
          <SessionWalk
            detail={detail}
            flashVisit={flash}
            flashNoyau={focusNoyau === 'determination'}
            {...(noyauTrial === undefined ? {} : { noyauTrial })}
            {...(rangementTrial === undefined ? {} : { rangementTrial })}
          />

          {trial}
        </>
      )}
      {tab === 'parcours' ? <SessionToc detail={detail} trials={tocTrials} /> : null}
    </div>
  );
}
